import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export type ExtensionHealthState = 'ok' | 'degraded' | 'logged_out' | 'unknown' | 'outdated';
export type ExtensionOverallState = ExtensionHealthState | 'offline';
export type ExtensionFix = 'login' | 'sync_tabs' | 'outdated' | 'unknown' | 'retry' | 'configure';

export interface ExtensionHealthCheck {
  key: string;
  label: string;
  ok: boolean;
  state: string;
  message: string;
  fix: ExtensionFix | null;
}

export interface ExtensionHealth {
  state: ExtensionHealthState;
  checkedAt: string | null;
  ok: number;
  total: number;
  checks: ExtensionHealthCheck[];
  fix: ExtensionFix | null;
  fixHint: string | null;
}

export interface ExtensionConnection {
  connectionId: string;
  marketplace: 'shopee' | 'mercadolivre';
  connectionName: string | null;
  providerAccountId: string | null;
  status: string;
  version: string | null;
  outdated: boolean;
  health: ExtensionHealth;
}

export interface TestResult {
  connectionId: string;
  success: boolean;
  message: string;
  online: boolean;
  shopId?: number;
  connectionName?: string | null;
}

const UNKNOWN_HEALTH: ExtensionHealth = { state: 'unknown', checkedAt: null, ok: 0, total: 0, checks: [], fix: 'unknown', fixHint: null };

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function normalizeConnection(c: any): ExtensionConnection {
  return {
    connectionId: c.connectionId,
    marketplace: c.marketplace || 'shopee',
    connectionName: c.connectionName ?? null,
    providerAccountId: c.providerAccountId ?? null,
    status: c.status || 'online',
    version: c.version ?? null,
    outdated: Boolean(c.outdated),
    health: c.health ? { ...UNKNOWN_HEALTH, ...c.health, checks: Array.isArray(c.health.checks) ? c.health.checks : [] } : UNKNOWN_HEALTH,
  };
}

export function summarizeExtensionState(connections: ExtensionConnection[]): ExtensionOverallState {
  if (!connections.length) return 'offline';
  const states = connections.map((c) => c.health.state);
  if (states.every((s) => s === 'ok')) return 'ok';
  if (states.some((s) => s === 'logged_out')) return 'logged_out';
  if (states.some((s) => s === 'degraded' || s === 'outdated')) return 'degraded';
  return 'unknown';
}

export function useMarketplaceConnections() {
  const { user } = useAuth();
  const [connections, setConnections] = useState<ExtensionConnection[]>([]);
  const [overall, setOverall] = useState<ExtensionOverallState>('offline');
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.tenantId) {
      setConnections([]);
      setOverall('offline');
      setLoading(false);
      return;
    }

    let active = true;

    const fetchExtensions = async () => {
      try {
        const res = await fetch('/api/shopee/browser-extension/status', { headers: authHeaders() })
          .then(r => r.ok ? r.json() : { connections: [] })
          .catch(() => ({ connections: [] }));

        if (!active) return;

        const extensionConnections = (res.connections || []).map(normalizeConnection);
        setConnections(extensionConnections);
        setOverall(res.overall || summarizeExtensionState(extensionConnections));
      } catch {
        if (active) { setConnections([]); setOverall('offline'); }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchExtensions();
    const timer = setInterval(fetchExtensions, 10000);
    return () => { active = false; clearInterval(timer); };
  }, [user?.tenantId]);

  const testExtension = useCallback(async (connectionId: string, marketplace?: string) => {
    setTesting(connectionId);
    setTestResult(null);
    try {
      const res = await fetch('/api/shopee/browser-extension/test', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ connectionId, marketplace: marketplace || 'shopee' }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err: any) {
      setTestResult({ connectionId, success: false, message: err.message || 'Erro de rede', online: false });
    } finally {
      setTesting(null);
    }
  }, []);

  // Pede a extensao para reverificar sessao/headers/tokens agora e atualiza a conexao na lista.
  const checkHealth = useCallback(async (connectionId: string, marketplace?: string) => {
    setChecking(connectionId);
    try {
      const res = await fetch('/api/shopee/browser-extension/health-check', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ connectionId, marketplace: marketplace || 'shopee' }),
      });
      const data = await res.json();
      if (data?.connection) {
        const updated = normalizeConnection(data.connection);
        setConnections((prev) => {
          const next = prev.map((c) => (c.connectionId === updated.connectionId && c.marketplace === updated.marketplace ? updated : c));
          setOverall(summarizeExtensionState(next));
          return next;
        });
      }
      return data;
    } catch (err: any) {
      return { success: false, message: err.message || 'Erro de rede' };
    } finally {
      setChecking(null);
    }
  }, []);

  return { connections, overall, loading, testExtension, testing, testResult, checkHealth, checking };
}
